import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import './AuthModal.css';

const TUTOR_CODE = '3choTut0r';

export default function AuthModal({ onClose, showToast }) {
  const [mode, setMode] = useState('signin'); // signin | signup | reset
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('student'); // student | tutor
  const [tutorCode, setTutorCode] = useState('');
  const [tutorCodeError, setTutorCodeError] = useState('');
  const [form, setForm] = useState({ email: '', password: '', fullName: '' });
  const [errors, setErrors] = useState({});
  const { signIn, signUp, resetPassword } = useAuth();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (mode !== 'reset') {
      if (!form.password) e.password = 'Password is required';
      else if (form.password.length < 6) e.password = 'Minimum 6 characters';
    }
    if (mode === 'signup' && !form.fullName) e.fullName = 'Name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Validate tutor code on signup
    if (mode === 'signup' && userType === 'tutor') {
      if (tutorCode !== TUTOR_CODE) {
        setTutorCodeError('Invalid tutor code. Please check with your coordinator.');
        return;
      }
      setTutorCodeError('');
    }

    setLoading(true);
    try {
      if (mode === 'signin') {
        const { error } = await signIn(form.email, form.password);
        if (error) throw error;
        showToast('Welcome back!', 'success');
        onClose();
      } else if (mode === 'signup') {
        const { error } = await signUp(form.email, form.password, form.fullName, userType);
        if (error) throw error;
        showToast(
          userType === 'tutor'
            ? 'Tutor account created! Check your email to confirm.'
            : 'Account created! Check your email to confirm.',
          'success'
        );
        onClose();
      } else {
        const { error } = await resetPassword(form.email);
        if (error) throw error;
        showToast('Password reset email sent!', 'success');
        setMode('signin');
      }
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        {/* Left panel */}
        <div className="auth-left">
          <img src="/echo-logo.png" alt="ECHO" className="auth-logo" />
          <div className="auth-tagline">
            <span className="auth-tagline-line">"Music washes away</span>
            <span className="auth-tagline-line">from the soul the</span>
            <span className="auth-tagline-line auth-tagline-em">dust of everyday life."</span>
          </div>
          <div className="auth-quote-attr">- Berthold Auerbach</div>
          <div className="auth-wave">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="auth-wave-bar" style={{ animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="auth-right">
          <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>

          <div className="auth-header">
            {mode !== 'reset' && (
              <div className="auth-mode-tabs">
                <button className={`auth-tab ${mode === 'signin' ? 'auth-tab--active' : ''}`} onClick={() => setMode('signin')}>Sign In</button>
                <button className={`auth-tab ${mode === 'signup' ? 'auth-tab--active' : ''}`} onClick={() => setMode('signup')}>Sign Up</button>
              </div>
            )}
            {mode === 'reset' && <h2 className="auth-title">Reset Password</h2>}
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <>
                <div className="form-group">
                  <label>Full Name</label>
                  <input type="text" placeholder="Jane Smith" value={form.fullName} onChange={e => set('fullName', e.target.value)} />
                  {errors.fullName && <span className="form-error">{errors.fullName}</span>}
                </div>

                {/* User type selector */}
                <div className="form-group">
                  <label>I am a...</label>
                  <div className="user-type-selector">
                    <button
                      type="button"
                      className={`user-type-btn ${userType === 'student' ? 'user-type-btn--active' : ''}`}
                      onClick={() => setUserType('student')}
                    >
                      <span className="user-type-icon">🎵</span>
                      <span className="user-type-label">Student</span>
                      <span className="user-type-desc">I want to learn music</span>
                    </button>
                    <button
                      type="button"
                      className={`user-type-btn ${userType === 'tutor' ? 'user-type-btn--active' : ''}`}
                      onClick={() => setUserType('tutor')}
                    >
                      <span className="user-type-icon">🎓</span>
                      <span className="user-type-label">Tutor</span>
                      <span className="user-type-desc">I want to teach music</span>
                    </button>
                  </div>
                </div>

                {/* Tutor code field */}
                {userType === 'tutor' && (
                  <div className="form-group">
                    <label>Tutor Access Code</label>
                    <input
                      type="password"
                      placeholder="Enter your tutor code"
                      value={tutorCode}
                      onChange={e => { setTutorCode(e.target.value); setTutorCodeError(''); }}
                    />
                    {tutorCodeError && <span className="form-error">{tutorCodeError}</span>}
                    <span className="form-hint">Contact your ECHO coordinator for your tutor code.</span>
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label>Email Address</label>
              <input type="email" placeholder="jane@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            {mode !== 'reset' && (
              <div className="form-group">
                <label>Password</label>
                <input type="password" placeholder="••••••••" value={form.password} onChange={e => set('password', e.target.value)} />
                {errors.password && <span className="form-error">{errors.password}</span>}
              </div>
            )}

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? <span className="auth-spinner" /> : null}
              {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Email'}
            </button>

            {mode === 'signin' && (
              <button type="button" className="auth-link-btn" onClick={() => setMode('reset')}>
                Forgot your password?
              </button>
            )}
            {mode === 'reset' && (
              <button type="button" className="auth-link-btn" onClick={() => setMode('signin')}>
                ← Back to Sign In
              </button>
            )}
          </form>

          {mode !== 'reset' && (
            <p className="auth-switch">
              {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
              <button className="auth-switch-btn" onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}>
                {mode === 'signin' ? 'Sign up for free' : 'Sign in'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
